export enum HierarchyAction {
  FOLDER = 'folder',
  DASHBOARD = 'dashboard',
  GROUP = 'group',
  RENAME = 'rename',
  DELETE = 'delete',
}

export type CreatableType = HierarchyAction.FOLDER | HierarchyAction.DASHBOARD | HierarchyAction.GROUP;

export interface HierarchyAccessors<T> {
  getId(node: T): string;
  getLabel(node: T): string;
  getChildren(node: T): T[] | undefined;
  isLeaf(node: T): boolean;
  isRoot(node: T): boolean;
  isDashboard(node: T): boolean;
  isGroup(node: T): boolean;
  // Inherited group ownership (walks ancestors) — used for readonly locks and the create payload's group_id.
  getGroupId(node: T): number | undefined;
  // True only for the node that IS the shared-branch root (owns the group_id itself) —
  // creates inside its descendants must keep their real parent id.
  isGroupRoot(node: T): boolean;
  equals(a: T, b: T): boolean;
  makeChild(created: { id: string; label: string; }, type: CreatableType, parent: T, childUrl?: string): T;
  insertChild(parent: T, child: T): void;
  // Insert a created child at the top level (parent is the — possibly synthetic — root
  // node that is absent from the tree). Optional: without it the reload picks it up.
  insertTopLevel?(nodes: T[], child: T): void;
}

export interface HierarchyDomain {
  createUrl(type: CreatableType): string;
  buildCreatePayload(args: { parentId: string; groupId?: number; isRoot: boolean; isGroupRoot: boolean; name: string; type: CreatableType; }): unknown;
  parseCreated(responseData: { id?: string; label?: string;[k: string]: unknown; }): { id: string; label: string; };
  modifyUrl(type: string, id: string): string;
  childUrl?(type: CreatableType, id: string): string;
  reload(): Promise<void>;
  actions: HierarchyAction[];
}

// Default create payload for dashboard-style hierarchies. `parent` is nulled only at
// the real roots (tree root / shared-branch root) — NOT for every group-owned node,
// or creates inside shared subfolders would land at the branch root. Groups always
// hang off their dashboard, so they keep the parent id unconditionally.
export const folderCreatePayload: HierarchyDomain['buildCreatePayload'] =
  ({ parentId, groupId, isRoot, isGroupRoot, name, type }) => ({
    label: name,
    parent: type !== HierarchyAction.GROUP && (isRoot || isGroupRoot) ? null : parentId,
    group_id: groupId,
  });

export interface TreeCtx<T> {
  getNodes(): T[];
  setNodes(nodes: T[]): void;
}
